import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useUIStore } from '@/state/store';
import { closeCallNotifications } from '@/lib/callNotifications';
import { chatTransport } from '@/lib/chatTransport';
import { useChatStore } from '@/state/chatStore';
import { ChatMirrorHost } from '@/lib/companionChatMirror';

interface IncomingCallEvent {
  callId: string;
  steleName: string;
  steleLocation?: string | null;
  tenantName: string;
  group: string;
  strategy: string;
  waitingMs: number;
}

/**
 * Hält eine Socket.io-Verbindung zum /agent-Namespace und routet eingehende
 * Events auf den UI-State.
 *
 * Verbindungsaufbau:
 *  - Beim Mount, falls schon ein Token in localStorage liegt (Reload-Szenario)
 *  - Beim Empfang des Custom-Events 'bamoa:logged-in' (frischer Login-Flow)
 *
 * Wir nutzen Custom-Events statt useEffect-Dependencies, weil localStorage
 * keine Re-Renders triggert und ein dauerndes Polling unschön wäre.
 */
export function useAgentSocket() {
  const setView = useUIStore((s) => s.setView);
  const showHeaderChip = useUIStore((s) => s.showHeaderChip);
  const socketRef = useRef<Socket | null>(null);
  const currentCallIdRef = useRef<string | null>(null);

  useEffect(() => {
    let heartbeat: ReturnType<typeof setInterval> | null = null;

    function getMyUserId() {
      return localStorage.getItem('bamoa_user_id');
    }

    function onMyStatus(e: any) {
      const status = e.detail?.status;
      if (status && socketRef.current) {
        socketRef.current.emit('status:set', { status });
      }
    }

    // pre140: Telefonie-Toggle des Beraters → an den Server melden.
    function onMyPhoneBusy(e: any) {
      const busy = e.detail?.busy === true;
      socketRef.current?.emit('phone:busy', { busy });
    }

    function connect() {
      const token = localStorage.getItem('bamoa_token');
      if (!token) {
        console.warn('[socket] kein Auth-Token, Verbindung wird nicht aufgebaut');
        return;
      }
      if (socketRef.current) {
        console.log('[socket] bereits verbunden, skip');
        return;
      }

      console.log('[socket] connecting...');
      const socket = io('/agent', {
        auth: { token },
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
      });
      socketRef.current = socket;

      // pre186: P2P-Chat-Transport an diese Sitzung binden (Signaling-Relay via chat:signal).
      const myId = getMyUserId();
      if (myId) chatTransport.attach(myId, (to, payload) => { try { socket.emit('chat:signal', { to, payload }); } catch { /* */ } });

      socket.on('connect', () => console.log('[socket] connected', socket.id));
      socket.on('disconnect', (reason) => console.log('[socket] disconnect', reason));

      // Welle 9: Single-Session — eine andere Agenten-Sitzung ist bereits aktiv.
      socket.on('session:conflict', (data) => {
        console.log('[socket] session:conflict', data);
        window.dispatchEvent(new CustomEvent('bamoa:session-conflict', { detail: data }));
      });
      // Diese Sitzung wurde an anderer Stelle übernommen → still beenden, NICHT reconnecten.
      socket.on('session:superseded', () => {
        console.log('[socket] session:superseded');
        try { socket.io.opts.reconnection = false; } catch { /* */ }
        try { socket.disconnect(); } catch { /* */ }
        socketRef.current = null;
        window.dispatchEvent(new Event('bamoa:session-superseded'));
      });
      // Aus dem Konflikt-Dialog gesteuert:
      const onTakeover = () => { try { socket.emit('session:takeover'); } catch { /* */ } };
      const onAbort = () => {
        try { socket.io.opts.reconnection = false; } catch { /* */ }
        try { socket.emit('session:abort'); } catch { /* */ }
        socketRef.current = null;
      };
      window.addEventListener('bamoa:do-session-takeover', onTakeover);
      window.addEventListener('bamoa:do-session-abort', onAbort);
      socket.on('disconnect', () => {
        window.removeEventListener('bamoa:do-session-takeover', onTakeover);
        window.removeEventListener('bamoa:do-session-abort', onAbort);
        useUIStore.getState().setCompanionConnected(false);
      });

      socket.on('hello', (data) => {
        console.log('[socket] hello', data);
        window.dispatchEvent(new Event('bamoa:session-ok'));
        // pre188: bei Reconnect/Reload mitten im Call NICHT auf 'available' zurücksetzen.
        const st = useUIStore.getState();
        if (!st.view.startsWith('incall') && st.view !== 'wrap') {
          socket.emit('status:set', { status: 'available' });
        }
      });

      socket.on('call:incoming', (data: IncomingCallEvent) => {
        console.log('[socket] call:incoming', data);
        currentCallIdRef.current = data.callId;
        (window as any).__incomingCall = data;
        useUIStore.getState().setIncomingCall({
          steleName: data.steleName, steleLocation: data.steleLocation, tenantName: data.tenantName, group: data.group,
        });
        setView('incoming');
      });

      // Welle 9: klingelnder Call in einer Gruppe (gruppenweite Sichtbarkeit/Pickup)
      socket.on('call:ringing', (data) => {
        window.dispatchEvent(new CustomEvent('bamoa:call-ringing', { detail: data }));
      });

      socket.on('call:pre-notification', (data) => {
        console.log('[socket] pre-notification', data);
      });

      socket.on('call:cancelled', (data) => {
        console.log('[socket] cancelled', data);
        window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: data }));
        closeCallNotifications(data.callId);
        if (currentCallIdRef.current === data.callId) {
          currentCallIdRef.current = null;
          setView('dashboard');
        }
      });

      socket.on('call:accepted', (data) => {
        console.log('[socket] accepted', data);
        window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: data }));
        closeCallNotifications(data.callId);
        // Call angenommen (egal wo, auch eigene andere Sitzung) → Incoming verlassen,
        // außer diese Sitzung ist selbst schon im Call (sie hat angenommen).
        const view = useUIStore.getState().view;
        if (currentCallIdRef.current === data.callId && !view.startsWith('incall')) {
          currentCallIdRef.current = null;
          if (view === 'incoming') setView('dashboard');
        }
      });

      socket.on('call:timeout', (data) => {
        window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: data }));
        closeCallNotifications(data.callId);
      });

      // Welle 9: Weiterleitung
      socket.on('call:transfer-offer', (data) => {
        console.log('[socket] call:transfer-offer', data);
        window.dispatchEvent(new CustomEvent('bamoa:transfer-offer', { detail: data }));
      });
      socket.on('call:transferred', (data) => {
        console.log('[socket] call:transferred', data);
        window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: data }));
        // Abgebender Berater verlässt den Call → Nachbereitung. Wichtig: still gehen
        // (transientes bye), damit die Stele auf den Übernehmer schwenkt statt zu beenden.
        if (data.fromUserId && data.fromUserId === getMyUserId() && currentCallIdRef.current === data.callId) {
          (window as any).__transferLeaving = true;
          currentCallIdRef.current = null;
          setView('wrap');
        }
      });
      socket.on('call:transfer-declined', (data) => {
        console.log('[socket] call:transfer-declined', data);
        window.dispatchEvent(new CustomEvent('bamoa:transfer-declined', { detail: data }));
      });

      // pre186: eingehendes Chat-Signaling (SDP/ICE) an den P2P-Transport reichen.
      socket.on('chat:signal', (data: { from?: string; payload?: any }) => {
        if (data?.from) void chatTransport.handleSignal(data.from, data.payload);
      });

      socket.on('agent:status', (data) => {
        console.log('[socket] peer status', data);
        window.dispatchEvent(new CustomEvent('bamoa:agent-status', { detail: data }));
        // pre196: eigene Status-Pille mitziehen, wenn der Status woanders (z. B. Companion)
        // geändert wurde. 'phone' läuft über agent:phone-status; incall/offline unverändert.
        if (data?.userId === getMyUserId()) {
          const s = (data as { status?: string }).status;
          if (s === 'available' || s === 'pause' || s === 'wrap') useUIStore.getState().setStatus(s);
        }
      });

      // pre140: Spiegelung des Telefonie-Status auf die eigene(n) Sitzung(en)
      // (A/V ↔ Companion). Server sendet an den user-Room beide Sitzungen.
      socket.on('agent:phone-status', (data: { userId: string; busy: boolean }) => {
        if (data?.userId === getMyUserId()) {
          useUIStore.getState().applyPhoneSignal(data.busy === true);
        }
      });

      // pre154: Companion-Präsenz (vom Backend an die A/V-Sitzung gespiegelt).
      socket.on('companion:presence', (data: { connected?: boolean }) => {
        useUIStore.getState().setCompanionConnected(!!data?.connected);
      });

      socket.on('call:ended', (data) => {
        console.log('[socket] call:ended', data);
        window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: data }));
        const inCallView = useUIStore.getState().view.startsWith('incall');
        if (inCallView) {
          currentCallIdRef.current = null;
          setView('wrap');
        }
      });

      // Welle 8d: Magic-Link — eingehende Beitrittsanfrage eines Gasts/Experten.
      // Kein View-Wechsel (der Berater ist im Call) → Overlay via Custom-Event.
      socket.on('join:request', (data) => {
        console.log('[socket] join:request', data);
        window.dispatchEvent(new CustomEvent('bamoa:join-request', { detail: data }));
      });
      // Welle 12 (pre125): Beitrittsanfrage erledigt (woanders zugelassen/abgelehnt) → Overlay schließen.
      socket.on('join:resolved', (data) => {
        window.dispatchEvent(new CustomEvent('bamoa:join-resolved', { detail: data }));
      });

      // Welle 12 (pre118): Companion-Remote (Variante A). Befehle vom Companion
      // → Window-Event; lokale A/V-Statusänderungen → an die Companion-Sitzung spiegeln.
      socket.on('remote:cmd', (data) => {
        window.dispatchEvent(new CustomEvent('bamoa:remote-cmd', { detail: data }));
      });
      const fwdRemoteState = (e: Event) => {
        try { socket.emit('remote:state', (e as CustomEvent).detail); } catch { /* */ }
      };
      window.addEventListener('bamoa:remote-state', fwdRemoteState);
      socket.on('disconnect', () => window.removeEventListener('bamoa:remote-state', fwdRemoteState));

      // pre190: Companion-Chat-Spiegel (A/V-Host). Beantwortet die Companion-Offer und
      // spiegelt den Chat P2P — der Server sieht nur SDP/ICE (huckepack), nie den Inhalt.
      const chatHost = new ChatMirrorHost(
        (sig) => { try { window.dispatchEvent(new CustomEvent('bamoa:remote-state', { detail: { __chat: sig } })); } catch { /* */ } },
        (c) => {
          if (c?.t === 'send') chatTransport.send(c.peerId, c.name ?? '', c.body ?? '');
          else if (c?.t === 'open') void chatTransport.ensureConnection(c.peerId);
          else if (c?.t === 'read') useChatStore.getState().markRead(c.peerId);
        },
      );
      chatHost.onOpen = () => {
        chatTransport.mirrorPush = (obj) => chatHost.send(obj);
        const cs = useChatStore.getState();
        chatHost.send({ t: 'snapshot', threads: cs.threads, names: cs.names, status: cs.status });
      };
      chatHost.onClose = () => { chatTransport.mirrorPush = null; };
      const onRemoteCmdForChat = (e: Event) => {
        const d = (e as CustomEvent).detail;
        if (d?.action === '__chat' && d.chat) void chatHost.onSignal(d.chat);
      };
      window.addEventListener('bamoa:remote-cmd', onRemoteCmdForChat);
      socket.on('disconnect', () => { window.removeEventListener('bamoa:remote-cmd', onRemoteCmdForChat); chatHost.close(); chatTransport.mirrorPush = null; });

      heartbeat = setInterval(() => {
        const state = useUIStore.getState();
        // pre188: nicht während eines Calls/der Nachbereitung 'available' senden —
        // sonst überschreibt der Heartbeat den serverseitigen incall/wrap-Status.
        if (state.status === 'available' && !state.view.startsWith('incall') && state.view !== 'wrap') {
          socket.emit('status:set', { status: 'available' });
        }
      }, 30000);
    }

    function onLoggedIn() {
      console.log('[socket] login event received → reconnecting');
      connect();
    }

    function onLoggedOut() {
      console.log('[socket] logout event → disconnecting');
      if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
      socketRef.current?.disconnect();
      socketRef.current = null;
    }

    // 1. Beim Mount versuchen (Reload-Szenario)
    connect();

    // 2. Listener für Login/Logout-Events
    window.addEventListener('bamoa:logged-in', onLoggedIn);
    window.addEventListener('bamoa:logged-out', onLoggedOut);
    window.addEventListener('bamoa:my-status', onMyStatus);
    window.addEventListener('bamoa:phone-busy', onMyPhoneBusy);

    return () => {
      if (heartbeat) clearInterval(heartbeat);
      window.removeEventListener('bamoa:logged-in', onLoggedIn);
      window.removeEventListener('bamoa:logged-out', onLoggedOut);
      window.removeEventListener('bamoa:my-status', onMyStatus);
      window.removeEventListener('bamoa:phone-busy', onMyPhoneBusy);
      socketRef.current?.disconnect();
      chatTransport.detach();
      socketRef.current = null;
    };
  }, [setView, showHeaderChip]);

  return {
    accept(callId: string) {
      socketRef.current?.emit('call:accept', { callId });
    },
    decline(callId: string) {
      socketRef.current?.emit('call:decline', { callId });
    },
    socket: () => socketRef.current,
  };
}
